import { useEffect, useRef } from "react";
import { drawSparkSprite } from "../lib/drawSpark.js";
import { appearAlpha, glowProfile, IDLE_LINKS, idleSparks, SPARK_CORE_TINT } from "../lib/spark.js";
import styles from "./SparkCanvas.module.css";

/** Sprite radius of each idle spark as a share of the shorter viewport side. */
const IDLE_RADIUS = 0.1;
/** Cluster centre as a share of the viewport height. */
const CLUSTER_Y = 0.55;
/** Halo brightness of the dim sparks, as a share of their usual glow. */
const DIM_GLOW = 0.55;
/** Hot centre of the dim sparks, so they stay visible without a core; peaks below opaque. */
const DIM_CENTRE = 1.6;
/** Sprite size of the bright spark, as a multiple of the others'. */
const BRIGHT_HALO = 1.6;
const LINK_WIDTH = 1;
const LINK_ALPHA = 0.45;
/** The show's link colour (uTint in lines.vert.glsl). */
const LINK_COLOR = `rgb(${SPARK_CORE_TINT.r}, ${SPARK_CORE_TINT.g}, ${SPARK_CORE_TINT.b})`;

/** Full-screen background canvas with a small cluster of sparks drifting in place. */
export function IdleSparksCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let width = 0;
    let height = 0;

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const start = performance.now();
    let frame = 0;
    const tick = (now: number): void => {
      ctx.clearRect(0, 0, width, height);
      const radius = Math.min(width, height) * IDLE_RADIUS;
      const alpha = appearAlpha(now - start);
      const sparks = idleSparks(now, motionQuery.matches ? 0 : undefined).map((spark) => ({
        ...spark,
        x: width / 2 + spark.dx * radius,
        y: height * CLUSTER_Y + spark.dy * radius,
      }));

      // Under the sprites, so the lines run into their cores.
      ctx.save();
      ctx.globalAlpha = alpha * LINK_ALPHA;
      ctx.strokeStyle = LINK_COLOR;
      ctx.lineWidth = LINK_WIDTH;
      ctx.beginPath();
      for (const [a, b] of IDLE_LINKS) {
        ctx.moveTo(sparks[a]!.x, sparks[a]!.y);
        ctx.lineTo(sparks[b]!.x, sparks[b]!.y);
      }
      ctx.stroke();
      ctx.restore();

      for (const spark of sparks) {
        const profile = glowProfile(spark.charge, now + spark.phaseMs);
        const bright = spark.charge >= 1;
        const scale = bright ? BRIGHT_HALO : 1;
        drawSparkSprite(
          ctx,
          spark.x,
          spark.y,
          radius * scale,
          {
            ...profile,
            intensity: bright ? profile.intensity : profile.intensity * DIM_GLOW,
            centre: bright ? profile.centre : profile.centre * DIM_CENTRE,
            spikes: bright ? profile.spikes : profile.spikes * DIM_GLOW,
          },
          alpha,
        );
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />;
}
